import React, { useEffect, useRef, useState } from 'react';
import { Switch, Route, Redirect, BrowserRouter } from 'react-router-dom';
import Tracker from '@openreplay/tracker';
import Modal from 'react-modal';

import './App.css';

import LoaderContext from './components/LoaderContext';

import UnreadCountBooter from './components/UnreadCountBooter';
import { UnreadCountProvider } from './components/UnreadCountContext';
import User from './components/Utils';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import MyPage from './pages/MyPage';
import UserPage from './pages/UserPage';
import CravingsPage from './pages/CravingsPage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import ChangeIdentityPage from './pages/ChangeIdentityPage';
import FaqPage from './pages/FaqPage';
import TermsPage from './pages/TermsPage';
import PolicyPage from './pages/PolicyPage';
import CashRewardClosedPage from './pages/CashRewardClosedPage';
import RestaurantPage from './pages/RestaurantPage';
import EatPage from './pages/EatPage';
import DrinksPage from './pages/DrinksPage';
import EatYolosPage from './pages/EatYolosPage';
import AddYoloPage from './pages/AddYoloPage';
import LoginPage from './pages/LoginPage';
import LogoutPage from './pages/LogoutPage';
import AdminPage from './pages/AdminPage';
import RestaurantSearch from './pages/RestaurantSearch';
import AccountSettingPage from './pages/AccountSettingPage';
import AdminCashRewardPage from './pages/AdminCashRewardPage';
import ClosedRestaurantsPage from './pages/ClosedRestaurantsPage'
import NewMemberPage from './pages/newMemberPage';
import RecentYollosPage from './pages/recentYollsPage';
import AdminRestorentPage from './pages/adminRestaurentPage';
import AdminRestorentDetailPage from './pages/adminRestaorentsDetailPage';
import FirstSplashScreen from './pages/FirstSplashScreen';
import SecondSplahScreen from './pages/SecondSplahScreen';
import VerificationSuccess from './pages/VerificationSuccess';
import DisclaimerPage from './pages/DisclaimerPage';
import ExpiredItemPage from './pages/ExpiredItemsPage';
import AdminItemNoLongerPage from './pages/adminItemNoLongerPage';
import UserSignupPage from './pages/UserSignupPage';
import UserLoginPage from './pages/UserLoginPage';
import LoginPagev2 from './pages/LoginPagev2';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminEmailExportPage from './pages/AdminEmailExportPage';
import AdminGiftCardSendListPage from './pages/AdminGiftCardSendListPage';
import AdminSuspiciousReferralPage from './pages/AdminSuspiciousReferralPage';
import NotifyPage from './pages/NotifyPage';
import CategoryPage from './pages/CategoryPage';
import AchievementsPage from './pages/AchievementsPage';
import AppBanner from './components/AppBanner';

Modal.setAppElement('#root');

type LocationExtras = {
  formattedAddress?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  placeId?: string | null;
  placeResponse?: string | null;
};

const isValidLocationObj = (locationObj: any): boolean => {
  if (!locationObj) return false;

  const lat = Number(locationObj.lat);
  const lng = Number(locationObj.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

  const EPS = 1e-6;
  if (Math.abs(lat) < EPS || Math.abs(lng) < EPS) return false;

  return true;
};

const pruneNulls = (obj: Record<string, any>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined));

function App() {
  const [user, setUser] = useState<User>({ isLogged: false, id: -1, name: '', email: '', token: '', expires: 0 });
  const userRef = useRef<User | null>(null);
  const [location, setLocation] = useState({ lat: 0, lng: 0, name: '' });
  const [locationExtras, setLocationExtras] = useState<LocationExtras>({
    formattedAddress: null,
    country: null,
    state: null,
    city: null,
    placeId: null,
    placeResponse: null
  });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef<boolean>(true);
  const [openReplayTracker, setOpenReplayTracker] = useState<any>(null);
  const hideLoader = () => { if (mountedRef.current) setLoading(false); };
  const showLoader = () => { if (mountedRef.current) setLoading(true); };

  const log = (...args: any[]) => {
    if (process.env.REACT_APP_LOG === 'true') console.log(...args);
  };
  const debug = (...args: any[]) => {
    if (process.env.REACT_APP_LOG === 'true') console.debug(...args);
  };

  useEffect(() => {
    mountedRef.current = true;
    const tracker = new Tracker({
      projectKey: 'DA2gV3NaLuEAJa8Jc4vM',
    });
    setOpenReplayTracker(tracker);
    return () => { mountedRef.current = false; };
  }, []);

  const getAuthUser = (maybeUser?: any) => {
    if (maybeUser && maybeUser.email) return maybeUser;
    if (userRef.current && userRef.current.email) return userRef.current;
    try {
      const stored = localStorage.getItem('YoloMeal');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.email) return parsed;
      }
    } catch (e) {}
    return null;
  };

  const saveLocationToDBIfLoggedIn = async (locationObj: any, loggedInUser?: any, extras?: LocationExtras) => {
    const authUser = getAuthUser(loggedInUser);
    if (!authUser?.email) {
      log('User not logged in — location not saved.');
      return;
    }

    if (!isValidLocationObj(locationObj)) {
      console.warn('Skipping save: invalid location (likely default 0,0 or missing) ->', locationObj);
      return;
    }

    const e: LocationExtras = extras ?? locationExtras ?? {};

    const basePayload: any = {
      userEmail: authUser.email,
      latitude: locationObj.lat,
      longitude: locationObj.lng
    };

    const extrasPayload = pruneNulls({
      formattedAddress: e.formattedAddress ?? null,
      country: e.country ?? null,
      city: e.city ?? null,
      state: e.state ?? null,
      placeId: e.placeId ?? null,
      placeResponse: e.placeResponse ?? null
    });

    const payload = { ...basePayload, ...extrasPayload };

    debug('saveLocationToDBIfLoggedIn -> payload:', payload);

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/save-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        log('Location saved');
      } else {
        console.error('Failed to save location — server returned error:', await res.text());
      }
    } catch (err) {
      console.error('Failed to save location:', err);
    }
  };

  const newLocation = async (name: string, position: any, extras?: LocationExtras, currentUser?: any) => {
    const minimalLocation = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      name,
    };

    if (mountedRef.current) setLocation(minimalLocation);
    try {
      localStorage.setItem('yolo.location', JSON.stringify(minimalLocation));
    } catch (e) {}

    if (extras) {
      if (mountedRef.current) setLocationExtras(extras);
      try {
        localStorage.setItem('yolo.locationExtras', JSON.stringify(extras));
      } catch (e) {}
    }

    const userToUse = currentUser ?? userRef.current;
    if (isValidLocationObj(minimalLocation)) {
      await saveLocationToDBIfLoggedIn(minimalLocation, userToUse, extras);
    } else {
      console.log('newLocation: invalid coords, skipping save to backend', minimalLocation);
    }
  };

  useEffect(() => {
    console.log(process.env.REACT_APP_VERSION);
    log(process.env.REACT_APP_SERVER_URL);
    loadUser();
    console.log(user.isLogged);
    userRef.current = user;

    const mapsSrcContains = (src: string) => src && src.indexOf('maps.googleapis.com/maps/api/js') !== -1;
    let googleMapScript = Array.from(document.getElementsByTagName('script')).find(s => mapsSrcContains(s.src));

    setLoading(true);
    log('loading google maps...');

    const onMapsLoaded = () => {
      log('map loaded');
      if (mountedRef.current) setLoading(false);

      try {
        const storedRaw = localStorage.getItem('yolo.location');
        debug('startup: yolo.location raw:', storedRaw);
        if (storedRaw) {
          try {
            const parsed = JSON.parse(storedRaw);
            if (isValidLocationObj(parsed)) {
              const extrasRaw = localStorage.getItem('yolo.locationExtras');
              debug('startup: yolo.locationExtras raw:', extrasRaw);
              if (extrasRaw) {
                try {
                  const parsedExtras = JSON.parse(extrasRaw);
                  if (parsedExtras && Object.keys(pruneNulls(parsedExtras)).length > 0) {
                    debug('startup: location + populated extras present — skipping geocode.');
                    if (mountedRef.current) {
                      setLocation(parsed);
                      setLocationExtras(parsedExtras);
                    }
                    return;
                  }
                } catch (e) {
                  console.warn('startup: failed parsing locationExtras - will geocode', e);
                }
              }
              debug('startup: coords present but extras missing — will run geocode.');
            } else {
              debug('startup: stored coords invalid — will run geocode.');
            }
          } catch (e) {
            console.warn('startup: failed parsing yolo.location - will run geocode', e);
          }
        }
      } catch (e) {
        console.error('startup: localStorage access failed', e);
      }

      if (!navigator.geolocation) {
        log('Geolocation not supported by browser');
        return;
      }

      navigator.geolocation.getCurrentPosition(async (position: any) => {
        if (!mountedRef.current) return;
        debug('GEOPOSITION:', position);

        const latlng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        if (!(window as any).google || !(window as any).google.maps || !(window as any).google.maps.Geocoder) {
          console.warn('Google Maps Geocoder not available on window.google');
          return;
        }
        const geocoder = new (window as any).google.maps.Geocoder();

        geocoder.geocode({ location: latlng }, async (results: any, status: any) => {
          debug('GEOCODER CALLBACK status:', status);
          debug('GEOCODER CALLBACK results (len):', results && results.length);
          debug('GEOCODER RAW[0]:', results && results[0]);

          if (!mountedRef.current) return;

          if (status === (window as any).google.maps.GeocoderStatus.OK) {
            if (results && results[0] && results[0]['address_components']) {
              const addressComponents = results[0]['address_components'];

              const country = addressComponents.filter((component: any) => component['types'].includes('country'));
              const state = addressComponents.filter((component: any) => component['types'].includes('administrative_area_level_1'));
              const locality = addressComponents.filter((component: any) => component['types'].includes('locality'));
              const administrativeArea = addressComponents.filter((component: any) => component['types'].includes('administrative_area_level_2'));

              const stateName = state.length > 0 ? (state[0].long_name || state[0].short_name) : (country.length > 0 ? (country[0].long_name || country[0].short_name) : '');
              const countryName = country.length > 0 ? (country[0].long_name || country[0].short_name) : '';

              let fullName: string = '';
              if (locality.length > 0 || administrativeArea.length > 0) {
                if (locality.length > 0) {
                  fullName = `${locality[0].long_name || locality[0].short_name}${stateName ? `, ${stateName}` : ''}${countryName ? `, ${countryName}` : ''}`;
                } else {
                  fullName = `${administrativeArea[0].long_name || administrativeArea[0].short_name}${stateName ? `, ${stateName}` : ''}${countryName ? `, ${countryName}` : ''}`;
                }

                const top = results[0];
                const formattedAddress = top?.formatted_address ?? null;
                const placeId = top?.place_id ?? null;

                let placeResponse: string | null = null;
                if (top) {
                  try { placeResponse = JSON.stringify(top); } catch (e) { placeResponse = null; }
                }

                const cityName: string | null = locality.length > 0
                  ? (locality[0].long_name || locality[0].short_name)
                  : (administrativeArea.length > 0 ? (administrativeArea[0].long_name || administrativeArea[0].short_name) : null);

                const minimalLocation = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                  name: fullName
                };

                const extras: LocationExtras = {
                  formattedAddress,
                  country: country.length > 0 ? (country[0].long_name || country[0].short_name) : null,
                  state: state.length > 0 ? (state[0].long_name || state[0].short_name) : null,
                  city: cityName,
                  placeId,
                  placeResponse
                };

                // merge/upgrade stored extras instead of letting old short_name block new long_name
                try {
                  const storedExtrasRaw = localStorage.getItem('yolo.locationExtras');
                  if (storedExtrasRaw) {
                    try {
                      const storedExtras = JSON.parse(storedExtrasRaw) || {};
                      if (Object.keys(pruneNulls(storedExtras)).length > 0) {
                        const mergedExtras = { ...storedExtras, ...extras };
                        debug('geocoder: merging storedExtras ->', { storedExtras, extras, mergedExtras });

                        if (mountedRef.current) {
                          setLocation(minimalLocation);
                          setLocationExtras(mergedExtras);
                        }
                        try { localStorage.setItem('yolo.location', JSON.stringify(minimalLocation)); } catch (e) {}
                        try { localStorage.setItem('yolo.locationExtras', JSON.stringify(mergedExtras)); } catch (e) {}

                        await saveLocationToDBIfLoggedIn(minimalLocation, undefined, mergedExtras);
                        return;
                      }
                    } catch (e) {
                      log('stored yolo.locationExtras parse failed -> will overwrite', e);
                    }
                  }
                } catch (e) {
                  log('localStorage read failed -> will overwrite', e);
                }

                // no usable stored extras — write fresh extras
                if (mountedRef.current) setLocation(minimalLocation);
                try { localStorage.setItem('yolo.location', JSON.stringify(minimalLocation)); } catch (e) {}

                if (mountedRef.current) setLocationExtras(extras);
                try { localStorage.setItem('yolo.locationExtras', JSON.stringify(extras)); } catch (e) {}

                await saveLocationToDBIfLoggedIn(minimalLocation, undefined, extras);
                return;
              }
            } else {
              log('Geocode was not successful for the following reason: ' + status);
            }
          } else {
            console.warn('Geocoder returned non-OK status:', status);
          }
        });
      }, (err) => {
        log('geolocation error', err);
      });
    };

    if (googleMapScript) {
      if ((window as any).google && (window as any).google.maps) {
        onMapsLoaded();
      } else {
        googleMapScript.addEventListener('load', onMapsLoaded);
      }
    } else {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      googleMapScript = document.createElement('script');
      googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=3`;
      googleMapScript.async = true;
      googleMapScript.defer = true;
      googleMapScript.addEventListener('load', onMapsLoaded);
      window.document.body.appendChild(googleMapScript);
    }

    return () => {
      mountedRef.current = false;
      try {
        if (googleMapScript) {
          googleMapScript.removeEventListener('load', onMapsLoaded);
        }
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const onLocChanged = (e: any) => {
      if (e?.detail) {
        if (mountedRef.current) setLocation(e.detail);
        try { localStorage.setItem('yolo.location', JSON.stringify(e.detail)); } catch {}
      }
    };
    window.addEventListener('yolo:locationChanged', onLocChanged);
    return () => window.removeEventListener('yolo:locationChanged', onLocChanged);
  }, []);

  const loadUser = () => {
    log('Reading from session...');
    const stored = localStorage.getItem('YoloMeal');
    log('stored YoloMeal:', stored);
    if (stored) {
      try {
        const userData = JSON.parse(stored);
        const expires = new Date(userData.expires);
        const now = new Date();
        if (expires > now) {
          const u = {
            isLogged: true,
            id: userData.id,
            name: userData.name,
            email: userData.email,
            token: userData.token,
            expires: userData.expires,
            profileUrl: userData.profileUrl ?? null,
          } as User;
          if (mountedRef.current) setUser(u);
          userRef.current = u;
        }
      } catch (e) {
        console.warn('Failed parse YoloMeal', e);
      }
    }
  };

  const handleLogin = async (u: any) => {
    try {
      const nextUser = {
        isLogged: true,
        id: u.id,
        name: u.name,
        email: u.email,
        token: u.token,
        expires: u.expires,
        profileUrl: u.profileUrl
      } as User;
      if (mountedRef.current) setUser(nextUser);
      userRef.current = nextUser;
      log('Saving to session... ' + u.name);
      localStorage.setItem('YoloMeal', JSON.stringify(nextUser));
      if (isValidLocationObj(location)) {
        await saveLocationToDBIfLoggedIn(location, nextUser);
      } else {
        log('Login: no valid location yet — skipping save on login.');
      }
    } catch (e) {
      console.error('handleLogin error', e);
    }
  };

  const onLoginBackClick = async () => { /* no-op for now */ };

  const handleLogout = async () => {
    const email = user.email;
    if (mountedRef.current) setUser({ isLogged: false, id: -1, name: '', email, token: '', expires: 0 });
    userRef.current = null;
    try { localStorage.setItem('YoloMeal', ''); } catch (e) {}
  };

  const handleLocationChange = async (obj: any) => {
    if (mountedRef.current) setLocation(obj);
    log(obj);
    debug('handleLocationChange ->', obj.name, obj.lng, obj.lat);
    try {
      if (obj && Number(obj.lat) && Number(obj.lng)) {
        localStorage.setItem('yolo.location', JSON.stringify(obj));
      } else {
        localStorage.removeItem('yolo.location');
      }
    } catch {}
    if (isValidLocationObj(obj)) {
      await saveLocationToDBIfLoggedIn(obj);
    } else {
      console.log('handleLocationChange: invalid location, not saving to server.');
    }
  };
  /*
  const loadMapApi = () => {
    console.log('loading google maps...');
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const mapsURL = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=3`;
    const scripts = document.getElementsByTagName('script');
    // Go through existing script tags, and return google maps api tag when found.
    for (let i = 0; i < scripts.length; i++) {
        if (scripts[i].src.indexOf(mapsURL) === 0) {
            return scripts[i];
        }
    }

    const googleMapScript = document.createElement('script');
    googleMapScript.src = mapsURL;
    googleMapScript.async = true;
    googleMapScript.defer = true;
    window.document.body.appendChild(googleMapScript);

    return googleMapScript;
  };
  */

  return (
    <LoaderContext.Provider value={{ loading, hideLoader, showLoader }}>
      {loading && (
        <div className="loader" id="loader">
          <div className="loader__holder">
            <h3>Loading...</h3>
            <div className="cover-spin"></div>
          </div>
        </div>
      )}
      <UnreadCountProvider>
        <UnreadCountBooter user={user} />
        <BrowserRouter>
          <Switch>
            <Route exact path="/">
              <Redirect to="/home" />
            </Route>
            <Route path="/home">
              {!loading && <HomePage user={user} location={location} handleLocationChange={handleLocationChange} />}
            </Route>
            <Route path="/about">
              <AboutPage user={user} />
            </Route>
            <Route path="/cash-reward">
              <CashRewardClosedPage user={user} />
            </Route>
            <Route path="/my-page">
              <MyPage user={user} handleLogin={handleLogin} />
            </Route>
          <Route path="/cravings">
            <CravingsPage user={user} handleLogin={handleLogin} />
             </Route>
            <Route path="/achievements">
              <AchievementsPage user={user} handleLogin={handleLogin}/>
            </Route>
            <Route path="/user/:id">
              <UserPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/settings">
              <SettingsPage user={user} />
            </Route>
            <Route path="/notifications">
              <NotificationsPage user={user} />
            </Route>
            <Route path="/change-identity">
              <ChangeIdentityPage user={user} />
            </Route>
            <Route path="/faq">
              <FaqPage user={user} />
            </Route>
            <Route path="/terms">
              <TermsPage user={user} />
            </Route>
            <Route path="/policy">
              <PolicyPage user={user} />
            </Route>
            <Route path="/login">
              <LoginPage
                user={user}
                handleLogin={handleLogin}
                showBack={false}
                showHome={true}
                backgroundImage={true}
                handleBack={onLoginBackClick}
              />
            </Route>
            <Route path="/forgot-password">
              <ForgotPasswordPage />
            </Route>
            <Route path="/reset-password">
              <ResetPasswordPage />
            </Route>

            <Route path="/signup">
              <UserSignupPage
                user={user}
                showHome={true}
                showBack={false}
                handleLogin={handleLogin}
                handleBack={onLoginBackClick}
                backgroundImage={true}
              />
            </Route>
            <Route path="/user-login">
              <UserLoginPage
                user={user}
                showHome={true}
                showBack={false}
                handleLogin={handleLogin}
                handleBack={onLoginBackClick}
                backgroundImage={true}
              />
            </Route>
            <Route path="/logout">
              <LogoutPage user={user} handleLogout={handleLogout} />
            </Route>
            <Route path="/restaurant/:id">
              <RestaurantPage user={user} handleLogin={handleLogin} restaurantPage={true} />
            </Route>
            <Route path="/add-yolo">
              <AddYoloPage
                user={user}
                location={location}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                handleLocationChange={handleLocationChange}
              />
            </Route>
            <Route path="/search-restaurant">{!loading && <RestaurantSearch user={user} location={location} handleLocationChange={handleLocationChange}/>}</Route>
            <Route path="/eat">
              <EatPage user={user} />
            </Route>
            <Route path="/drinks">
              <DrinksPage user={user} />
            </Route>
            <Route path="/entree">
              <EatYolosPage
                name="Entree"
                id={1}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/pizza">
              <EatYolosPage
                name="Pizza"
                id={3}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/burgers">
              <EatYolosPage
                name="Burgers"
                id={2}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/handheld">
              <EatYolosPage
                name="handheld"
                id={13}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/dessert">
              <EatYolosPage
                name="Dessert"
                id={6}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/breakfast">
              <EatYolosPage
                name="Breakfast"
                id={7}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/apps">
              <EatYolosPage
                name="Appetizer"
                id={4}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/sides">
              <EatYolosPage
                name="Sides"
                id={5}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/fast-food">
              <EatYolosPage
                name="Fast Food"
                id={14}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="eat"
              />
            </Route>
            <Route path="/wine">
              <EatYolosPage
                name="Wine"
                id={8}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="drink"
              />
            </Route>
            <Route path="/mixed-drinks">
              <EatYolosPage
                name="Mixed Drinks"
                id={9}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="drink"
              />
            </Route>
            <Route path="/beer">
              <EatYolosPage
                name="Beer"
                id={10}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="drink"
              />
            </Route>
            <Route path="/coffee">
              <EatYolosPage
                name="Coffee"
                id={11}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="drink"
              />
            </Route>
            <Route path="/other-drinks">
              <EatYolosPage
                name="Other Drinks"
                id={12}
                location={location}
                user={user}
                handleLogin={handleLogin}
                eatOrMeal="drink"
              />
            </Route>
            <Route path="/categories">
              <CategoryPage
                location={location}
                user={user}
                handleLogin={handleLogin}
              />
            </Route>
            <Route path="/admin">
              <AdminPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/cashRewards">
              <AdminCashRewardPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/giftCardList">
              <AdminGiftCardSendListPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/suspicious-referrals">
              <AdminSuspiciousReferralPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/findemails">
              <AdminEmailExportPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/newMembers">
              <NewMemberPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/recentYollos">
              <RecentYollosPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/markedClosedRestaurant">
              <ClosedRestaurantsPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/markedItemExpired">
              <ExpiredItemPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/itemDetails/:id">
              <AdminItemNoLongerPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/restaurent">
              <AdminRestorentPage
                user={user}
                handleLogin={handleLogin}
                location={location}
                handleLocationChange={handleLocationChange}
              />
            </Route>
            <Route path="/restaurentDetail/:id">
              <AdminRestorentDetailPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/account">
              <AccountSettingPage user={user} handleLogin={handleLogin}></AccountSettingPage>
            </Route>
            <Route path="/contest1">
              <FirstSplashScreen></FirstSplashScreen>
            </Route>
            <Route path="/contest2">
              <SecondSplahScreen></SecondSplahScreen>
            </Route>
            <Route path="/loginv2">
              <LoginPagev2
                user={user}
                emailVerified={false}
                handleLogin={handleLogin}
                showHome={true}
                handleBack={onLoginBackClick}
                success={() => {}}
              />
            </Route>
            <Route path="/verification-success">
              <VerificationSuccess user={user} handleLogin={handleLogin} handleBack={onLoginBackClick} />
            </Route>
            <Route path="/notify">
              <NotifyPage user={user} handleLogin={handleLogin} />
            </Route>
            <Route path="/contest_disclaimer_and_conditions">
              <DisclaimerPage></DisclaimerPage>
            </Route>
          </Switch>
        </BrowserRouter>
        <AppBanner />
      </UnreadCountProvider>
    </LoaderContext.Provider>
  );
}

export default App;
